import { appUrl, canvaAppUrl, powerpointAppUrl, tolstoyAppUrl } from '@/environment';
import { useAnalytics } from '@/services/analytics';
import { useFlagExperiment } from '@/services/featureFlags';
import styled from '@emotion/styled';
import { ReactNode } from 'react';
import { AppButton } from './AppButton';
import canva from './images/canva.svg';
import logo from './images/logo.png';
import powerpoint from './images/powerpoint.png';
import tolstoy from './images/tolstoy.svg';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
    flex-wrap: wrap;
`;

const ItemsContainer = styled.div`
    display: flex;
    position: relative;
    outline: none;
    gap: 16px;
    flex-wrap: wrap;
`;

const SectionTitle = styled.h1`
    color: ${({ theme }) => theme.colors.black[700]};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.32px;
`;

export interface App {
    id: string;
    icon: string;
    label: string;
    description: string;
    onClick?: () => void;
    children?: ReactNode;
}

export const AppsPage = () => {
    const mixpanel = useAnalytics();
    const tolstoyEnabled = useFlagExperiment('tolstoy_enabled', false);

    const apps: App[] = [
        {
            id: 'apps-mobile-app-ios',
            icon: logo,
            label: 'Mobile App',
            description: 'Create Avatar videos on the go with the D-ID Studio mobile app',
            onClick: async () => {
                mixpanel?.track('apps-mobile-app', { event: 'click', from: 'apps-page' });
                window.open(appUrl, '_blank');
            },
        },
        {
            id: 'apps-canva',
            icon: canva,
            label: 'Canva',
            description: 'Elevate your Canva designs with AI Avatars',
            onClick: async () => {
                mixpanel?.track('apps-canva', { event: 'click', from: 'apps-page' });
                window.open(canvaAppUrl, '_blank');
            },
        },
        {
            id: 'apps-powerpoint',
            icon: powerpoint,
            label: 'PowerPoint (desktop)',
            description: 'Amaze your audience with a presentations featuring AI Avatars',
            onClick: async () => {
                mixpanel?.track('apps-powerpoint', { event: 'click', from: 'apps-page' });
                window.open(powerpointAppUrl, '_blank');
            },
        },
        ...(tolstoyEnabled
            ? [
                  {
                      id: 'apps-tolstoy',
                      icon: tolstoy,
                      label: 'Tolstoy AI Video Player',
                      description: 'Boost conversions by adding avatar videos to your online sales platform',
                      onClick: async () => {
                          mixpanel?.track('apps-tolstoy', { event: 'click', from: 'apps-page' });
                          window.open(tolstoyAppUrl, '_blank');
                      },
                  },
              ]
            : []),
    ];

    return (
        <Container>
            <SectionTitle>Apps</SectionTitle>
            <ItemsContainer>
                {apps.map(app => (
                    <AppButton key={app.id} app={app} />
                ))}
            </ItemsContainer>
        </Container>
    );
};
